import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Card from 'components/Account/Common/Card/Card';
import DetailsProductsTableFooter from 'components/Account/MyOrder/OrderDetails/DetailsProducts/DetailsProductsTableFooter/DetailsProductsTableFooter';
import TrackingDetails from 'components/Account/MyOrder/OrderDetails/DetailsProducts/Traking/TrackingDetails';
import { hasTrackingLink } from 'components/Account/MyOrder/OrderDetails/DetailsProducts/Traking/trackingUtils';
import ReturnLink, {
  returnLinkByMethod,
} from 'components/Account/MyOrder/OrderDetails/DetailsProducts/ReturnLink';
import { DataDetailledOrder } from 'types/DetailsProducts';
import { formatPrice } from 'services/formatter';

import styles from './DetailsProductsMobile.module.scss';

export default function DetailsProductsMobile({
  order,
}: {
  order: DataDetailledOrder;
}): JSX.Element {
  const { t } = useTranslation(['ordersPage']);
  const {
    idOrder,
    date,
    paymentMethod,
    deliveryMethod,
    products,
    subTotalOrder,
    subTotalDiscount,
    FPORT,
    FSIMO,
    FCR,
    FDOUA,
    REM,
    MTT_TOTAL,
  } = order;

  const finalCosts = [
    subTotalDiscount,
    subTotalOrder,
    FPORT,
    FSIMO,
    FCR,
    FDOUA,
    REM,
    MTT_TOTAL,
    paymentMethod,
    deliveryMethod,
  ];

  const methodHasTracking = hasTrackingLink(deliveryMethod || '');
  const methodHasReturnLink = !!returnLinkByMethod[deliveryMethod ?? ''];

  function formatLabel(label: string) {
    return t('LABEL_TWO_POINTS', { label: label, ns: 'translation' });
  }

  return (
    <Card classes={{ container: styles.card }}>
      <div className={styles.titleArea}>
        {t('LABEL_ORDER_NUMBER_ORDER')}
        {t('LABEL_ORDER_ID_AND_DATE', { value: idOrder, date })}
      </div>
      <div className={styles.content}>
        {products.map(({ discount, idColis, status, ...product }) => (
          <ul key={product.refCatalog}>
            <li className={styles.border}>
              <b>{formatLabel(t('LABEL_ORDER_ITEM'))}</b>
              <span>{product.libelle}</span>
            </li>
            <li>
              <b>{formatLabel(t('LABEL_ORDER_PRICE'))}</b>
              <span>{formatPrice(product.productPrice)}</span>
            </li>
            <li>
              <b>{formatLabel(t('LABEL_ORDER_QUANTITY'))}</b>
              <span>{product.quantity}</span>
            </li>
            {parseFloat(discount) > 0 && (
              <li>
                <b>{formatLabel(t('LABEL_ORDER_DISCOUNT'))}</b>
                <span>{formatPrice(`-${discount}`)}</span>
              </li>
            )}
            <li>
              <b>{formatLabel(t('LABEL_ORDER_SUBTOTAL'))}</b>
              <span>{formatPrice(product.subTotalProduct)}</span>
            </li>
            <li>
              <b>{formatLabel(t('LABEL_ORDER_STATEARTICLE'))}</b>
              <span>
                {methodHasTracking && idColis && (
                  <TrackingDetails
                    deliveryMethod={deliveryMethod || ''}
                    trackingNumber={idColis}
                  />
                )}
                <span
                  className={classNames({
                    [styles.state]: methodHasTracking && idColis,
                  })}
                >
                  {status}
                </span>
              </span>
            </li>
            {deliveryMethod && methodHasReturnLink && idOrder && (
              <li>
                <b>{formatLabel(t('LABEL_ORDER_EASYRETURN_TITLE'))}</b>
                <span>
                  {t('LABEL_ORDER_EASYRETURN_LABEL')}
                  <ReturnLink
                    deliveryMethod={deliveryMethod}
                    orderId={idOrder}
                  />
                </span>
              </li>
            )}
          </ul>
        ))}
        <DetailsProductsTableFooter
          component={TableFooterComponent}
          finalCosts={finalCosts}
          nbColumns={2}
        />
      </div>
    </Card>
  );
}

function TableFooterComponent({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  return <table className={styles.tableFooter}>{children}</table>;
}
